"use client";

import { createPortal } from "react-dom";
import { Gamja_Flower } from "next/font/google";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import RedAlert from "@/components/RedAlert";
import Banner from "@/components/Banner";
import TestCenterMapClient from "./TestCenterMapClient";
import { useTestCenterShellSlots } from "@/components/test-center-shell/TestCenterShellContext";

const gamjaFlower = Gamja_Flower({
  weight: "400",
  subsets: ["latin"],
  display: "swap",
  fallback: ["serif"],
});

/**
 * 지도 위 카드 2개 — 원서접수 일정 / 실기 후기.
 * vertical=true(셸 좌측 사이드바): full-width 세로 스택. false(모바일/클래식): 기존 가로 2열.
 */
function InfoCards({ vertical = false }: { vertical?: boolean }) {
  return (
    <div
      className={cn(
        vertical
          ? "flex w-full flex-col gap-2"
          : "flex items-center justify-center gap-2",
      )}
    >
      <Link
        href="/exam-registration/jeongcheogi-application"
        passHref
        rel="noopener"
        className="w-full max-w-2xl"
      >
        <Button
          variant="outline"
          className={cn(
            "h-13 cursor-pointer bg-amber-50 text-sm font-semibold text-foreground dark:bg-amber-950 md:text-base",
            vertical ? "w-full justify-start" : "w-[180px] md:w-full",
          )}
        >
          <Image
            src="/theory/moodang_gamza.webp"
            alt="2026년 3회 정보처리기사 원서접수 일정"
            width={40}
            height={40}
            className={vertical ? "mr-3" : "mr-0 md:mr-3"}
          />
          <span className={vertical ? "text-wrap" : "text-wrap md:text-nowrap"}>
            26년 3회 정처기 원서접수 일정
          </span>
        </Button>
      </Link>
      <Link
        href="/past-exam/2026/2026-2-review"
        passHref
        rel="noopener"
        className="w-full max-w-2xl"
      >
        <Button
          variant="outline"
          className={cn(
            "h-13 cursor-pointer bg-amber-50 text-sm font-semibold text-foreground dark:bg-amber-950 md:text-base",
            vertical ? "w-full justify-start" : "w-[180px] md:w-full",
          )}
        >
          <Image
            src="/theory/pray_gamza.webp"
            alt="2026년 2회 정보처리기사 실기 후기 + 합격률"
            width={40}
            height={40}
            className={vertical ? "mr-3" : "mr-0 md:mr-3"}
          />
          <span className={vertical ? "text-wrap" : "text-wrap md:text-nowrap"}>
            26년 2회 정처기 실기 후기
          </span>
        </Button>
      </Link>
    </div>
  );
}

function BannerHeading({ compact = false }: { compact?: boolean }) {
  return (
    <h2
      className={cn(
        "text-center font-semibold",
        gamjaFlower.className,
        // compact(셸 우측 사이드바): 좁은 폭에서 한 줄로 보이도록 축소
        compact ? "whitespace-nowrap text-base" : "text-2xl",
      )}
    >
      정보처리기사{" "}
      <span className="bg-amber-100 dark:bg-amber-900">합격</span> 도와줄라고
      하는 감자
    </h2>
  );
}

/**
 * 개설 예정 시험장 안내 (회차·기준 일시).
 *
 * 셸 우측 슬롯과 모바일/클래식 양쪽에서 같은 문구를 쓰고 className만 다르다.
 * 큐넷이 접수 기간 중 기준 일시를 갱신하므로 문구를 한 곳에만 둔다.
 */
function ScheduleAlert({ className }: { className?: string }) {
  return (
    <RedAlert
      title="2026년 3회 실기 개설 예정 시험장입니다."
      description="9월 18일 10시 기준 공개 예정 현황이며, 실제 접수시 변경될 수 있으니 참고용으로!"
      className={className}
    />
  );
}

/**
 * 시험장 찾기 본문 (JCG-2100).
 *
 * 데스크탑 셸(TestCenterShell)에서는 카드+RedAlert+배너를 우측 슬롯으로 portal 하고
 * 가운데엔 제목+지도만 둔다(좌측은 아이콘 레일만 → 지도가 넓다).
 * 셸 밖(모바일/클래식)에서는 기존 단일 컬럼 그대로 렌더.
 */
export default function TestCenterContent() {
  const { inShell, rightSlot } = useTestCenterShellSlots();

  const heading = (
    <h1
      id="article-h1"
      className={`text-center text-2xl font-semibold ${gamjaFlower.className} mt-6`}
    >
      정보처리기사 <span className="bg-amber-100 dark:bg-amber-900">시험장</span>{" "}
      찾기
    </h1>
  );

  if (inShell) {
    return (
      <>
        {/* 가운데: 제목 + 지도(좌측 콘텐츠 컬럼이 없어 폭이 넓다) */}
        {heading}
        <div className="mt-4">
          <TestCenterMapClient />
        </div>

        {/* 우측 사이드바: 카드 2개 + RedAlert(맨 위) → 합격 배너(스택) */}
        {rightSlot &&
          createPortal(
            <>
              <InfoCards vertical />
              <ScheduleAlert className="w-full" />
              <div className="flex flex-col gap-3">
                <BannerHeading compact />
                <Banner stacked />
              </div>
            </>,
            rightSlot,
          )}
      </>
    );
  }

  // 클래식/모바일 — 기존 단일 컬럼 레이아웃
  return (
    <div className="flex flex-col items-center px-4">
      {heading}

      <div className="mt-4 w-full max-w-2xl">
        <InfoCards />
      </div>

      <ScheduleAlert className="mt-4 w-auto mx-auto" />

      <TestCenterMapClient />

      <div className="mt-6">
        <BannerHeading />
      </div>
      <Banner />
    </div>
  );
}
